<template>
  <v-dialog
    :model-value="modelValue"
    max-width="460"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card">
      <div class="dialog-header d-flex align-center justify-space-between">
        <div class="d-flex align-center ga-3">
          <div
            class="dialog-header__icon"
            :class="isActive ? 'dialog-header__icon--warning' : 'dialog-header__icon--success'"
          >
            <v-icon :color="isActive ? 'warning' : 'success'" size="22">
              {{ isActive ? 'mdi-account-off-outline' : 'mdi-account-check-outline' }}
            </v-icon>
          </div>
          <span class="dialog-header__title">{{ dialogTitle }}</span>
        </div>
        <v-btn
          icon
          variant="text"
          size="small"
          class="dialog-close-btn"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body dialog-body--message">
        <template v-if="isActive">
          <strong class="dialog-body__name">{{ user?.name }}</strong>
          ({{ user?.email }}) will lose portal access until reactivated.
        </template>
        <template v-else>
          Restore portal access for
          <strong class="dialog-body__name">{{ user?.name }}</strong>
          ({{ user?.email }})?
        </template>
      </v-card-text>

      <v-card-actions class="dialog-footer">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-none dialog-btn dialog-btn--muted"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          :color="isActive ? 'warning' : 'success'"
          class="text-none dialog-btn"
          rounded="lg"
          :loading="saving"
          @click="confirm"
        >
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import orgUsersApi from '@/mixins/Users/orgUsersApi'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'

export default {
  name: 'ToggleUserStatusDialog',
  mixins: [orgUsersApi],
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      saving: false,
    }
  },
  computed: {
    isActive() {
      return String(this.user?.status ?? '').toUpperCase() === 'ACTIVE'
    },
    dialogTitle() {
      return this.isActive ? 'Deactivate team member' : 'Activate team member'
    },
    confirmLabel() {
      return this.isActive ? 'Deactivate' : 'Activate'
    },
    nextStatus() {
      return this.isActive ? 'INACTIVE' : 'ACTIVE'
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    async confirm() {
      if (!this.user?.id) return

      this.saving = true
      const response = await this.updateOrgUser({
        user_id: this.user.id,
        user_status: this.nextStatus,
      })
      this.saving = false

      if (isApiSuccess(response)) {
        this.$emit('saved', response)
        this.close()
      } else {
        this.$emit('error', response)
      }
    },
  },
}
</script>

<style scoped>
.dialog-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--y-radius-md);
}

.dialog-header__icon--warning {
  background: rgba(245, 158, 11, 0.14);
}

.dialog-header__icon--success {
  background: rgba(16, 185, 129, 0.14);
}
</style>
